import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SkeletonLoaderComponent } from '@shared/components/skeleton-loader/skeleton-loader.component';
import { ISkeletonColumn } from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { ColorUtilitiesService } from '@utilities/color-utilities.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { GraphUtilitiesService } from '@utilities/graph-utilities.service';
import { NumberUtilitiesService } from '@utilities/number-utilities.service';
import { addHours, subHours } from 'date-fns';
import type { DataZoomComponentOption, EChartsOption, LineSeriesOption, SeriesOption } from 'echarts';
import type { ECElementEvent, EChartsType } from 'echarts/core';
import { TitleOption } from 'echarts/types/dist/shared';
import { NgxEchartsDirective } from 'ngx-echarts';
import { NgxFloatUiModule } from 'ngx-float-ui';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FindingSpansChart } from '../finding-spans-chart';
import { IFindingSpans } from '../graph-finding-spans.interface';
import { getFindingSpansLegend, sortFindingSpans } from '../graph-finding-spans.util';
import { getNextDotIndex, toDotIndex, toSeriesDataIndex } from '../graph-pair-mode.util';
import {
  IChartClickParams,
  ITimelineGraphData,
  ITimelineGraphInputData,
  TTimelineGraphSeriesItem,
} from './timeline-graph.interface';

@Component({
  imports: [
    NgxEchartsDirective,
    NgxFloatUiModule,
    MatButtonModule,
    SkeletonLoaderComponent,
    IconComponent,
    NgxSkeletonLoaderModule,
  ],
  selector: 'app-timeline-graph',
  templateUrl: './timeline-graph.component.html',
})
export class TimelineGraphComponent {
  public readonly dotGraphData = input<ITimelineGraphInputData[]>([]);
  public readonly chartConfiguration = input<Partial<EChartsOption>>({});
  public readonly pairMode = input<boolean>(false);
  // Replaces the dots: each finding drawn as a bar from start to end on a track, with its number above.
  public readonly findingSpans = input<IFindingSpans | null>(null);
  public readonly startTimestamp = input<number | null>(null);
  public readonly endTimestamp = input<number | null>(null);

  public readonly pointSelected = output<string>();

  public readonly chartOption = signal<EChartsOption>({});
  public readonly chartFinished = signal(false);

  public readonly skeletonGraphColumns: ISkeletonColumn[] = [
    {
      className: 'w-full',
      perRow: [
        { height: '150px', type: 'text' },
        { height: '30px', type: 'text' },
      ],
      rowMargin: '0',
      rowPadding: '6px 0',
      type: 'text',
    },
  ];

  public readonly skeletonGraphRows = 2;

  private readonly processedDotGraphData = signal<ITimelineGraphData[]>([]);

  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly numberUtils = inject(NumberUtilitiesService);
  private readonly translate = inject(TranslateService);
  private readonly graphUtilitiesService = inject(GraphUtilitiesService);
  private readonly findingSpansChart = new FindingSpansChart();

  private readonly viewersLabel = this.translate.instant('pages.campaignDetails.ctaAnalytics.details.graph.viewers');
  private readonly videoTimeMode = computed(() => this.dotGraphData().every((item) => item.videoTime !== undefined));

  private chartInstance: EChartsType | null = null;
  private minTimestamp = 0;
  private maxTimestamp = 0;
  private selectedPointDataIndex: number | null = null;

  constructor() {
    this.initializeChart();

    effect(() => {
      const dotGraphData = this.dotGraphData();
      const isPairMode = this.pairMode();

      const formattedDotGraphData = dotGraphData.map((item) => ({
        ...item,
        formattedTime: this.videoTimeMode()
          ? DurationUtilitiesService.convertSecondsToVideoTime(item.xAxisValue, true)
          : this.dateUtils.formatDate(new Date(item.xAxisValue), 'yyyy-MM-dd HH:mm'),
        status: item.status,
        timestamp: item.xAxisValue,
        value: 1,
      }));

      if (isPairMode && dotGraphData.length % 2 !== 0) {
        this.processedDotGraphData.set(formattedDotGraphData.slice(0, -1));
      } else {
        this.processedDotGraphData.set(formattedDotGraphData);
      }

      this.initializeChart();
    });
  }

  public onChartInit(chart: EChartsType): void {
    this.chartInstance = chart;
    this.setupChartClickListener(chart);
    chart.on('rendered', () => this.findingSpansChart.updateLabels(chart));
  }

  public onChartClick(event: ECElementEvent | undefined): void {
    if (this.findingSpans() && event?.event) {
      const spanId = this.findingSpansChart.select(this.chartInstance, event);

      if (spanId) {
        this.pointSelected.emit(spanId);
      }

      return;
    }

    if (typeof event?.dataIndex !== 'number' || !event?.event) {
      this.selectedPointDataIndex = null;

      return;
    }

    const { dataIndex, name } = event;
    this.selectedPointDataIndex = toDotIndex(dataIndex, this.pairMode());

    const clickedDataId = name;

    if (clickedDataId) {
      this.pointSelected.emit(clickedDataId);
    }
  }

  public selectNextPoint(): void {
    if (this.findingSpans()) {
      this.selectNextFindingSpan();

      return;
    }

    if (!this.chartInstance || this.processedDotGraphData().length === 0) {
      return;
    }

    const nextIndex: number = this.calculateNextIndex();
    this.selectedPointDataIndex = nextIndex;

    const selectedId = this.processedDotGraphData()[nextIndex].id;
    this.pointSelected.emit(selectedId);

    const selectedTimestamp = this.processedDotGraphData()[nextIndex].timestamp;
    this.panDataZoomToTimestamp(selectedTimestamp);

    this.chartInstance.dispatchAction({
      dataIndex: toSeriesDataIndex(nextIndex, this.pairMode()),
      seriesIndex: 1,
      type: 'showTip',
    });
  }

  public onChartFinished(): void {
    this.chartFinished.set(true);
  }

  private selectNextFindingSpan(): void {
    if (!this.chartInstance) {
      return;
    }

    const step = this.findingSpansChart.selectNext();

    if (!step) {
      return;
    }

    this.pointSelected.emit(step.span.id);
    this.panDataZoomToTimestamp(step.span.start);
    this.chartInstance.dispatchAction({ dataIndex: step.dataIndex, seriesIndex: step.seriesIndex, type: 'showTip' });
  }

  private initializeChart(): void {
    this.calculateDataRange();

    const findingSpans = this.findingSpans();
    const newOptions: EChartsOption = {
      dataZoom: this.getDataZoomConfig(),
      grid: this.getGridConfig(),
      ...(findingSpans?.items.length ? { legend: getFindingSpansLegend(findingSpans.legend) } : {}),
      series: this.getSeriesConfig(),
      title: {
        left: 0,
        text:
          (this.chartConfiguration()?.title as TitleOption)?.text ??
          this.translate.instant('pages.campaignDetails.ctaAnalytics.details.graph.viewerCountOverTime'),
        textStyle: {
          color: ColorUtilitiesService.shikensoPurpleBase,
          fontSize: 12,
          fontWeight: 'normal',
        },
        top: 0,
      },
      tooltip: {
        axisPointer: {
          lineStyle: {
            color: ColorUtilitiesService.shikensoPinkBase,
            type: 'dashed',
          },
          type: 'line',
        },
        formatter: this.getTooltipFormatter.bind(this),
        trigger: 'axis',
      },
      xAxis: this.getXAxisConfig(),
      yAxis: this.getYAxisConfig(),
    };

    this.chartOption.set(newOptions);
  }

  private setupChartClickListener(chart: EChartsType): void {
    chart.getZr().on('click', (params: IChartClickParams) => {
      const pointInPixel = [params.offsetX, params.offsetY];

      if (!chart.containPixel('grid', pointInPixel)) {
        this.selectedPointDataIndex = null;
        this.findingSpansChart.clearSelection();
      }
    });
  }

  private calculateDataRange(): void {
    const data = this.processedDotGraphData();
    const spans = this.findingSpans()?.items ?? [];

    if (!data?.length && !spans.length) {
      if (this.videoTimeMode()) {
        this.minTimestamp = this.startTimestamp() ?? 0;
        this.maxTimestamp = this.endTimestamp() ?? 11;
      } else {
        const now = Date.now();
        this.minTimestamp = subHours(now, 1).getTime();
        this.maxTimestamp = addHours(now, 1).getTime();
      }

      return;
    }

    const timestamps = [...data.map((item) => item.timestamp), ...spans.flatMap((span) => [span.start, span.end])];
    let [minTs] = timestamps;
    let [maxTs] = timestamps;

    for (const timestamp of timestamps) {
      if (timestamp < minTs) minTs = timestamp;

      if (timestamp > maxTs) maxTs = timestamp;
    }

    const allImages = data.length > 0 && data.every((item) => item.isImage);
    this.minTimestamp = allImages ? 0 : (this.startTimestamp() ?? (minTs || 1) - 1);
    this.maxTimestamp = this.endTimestamp() ?? maxTs + 1;
  }

  private getGridConfig(): EChartsOption['grid'] {
    return {
      bottom: 72,
      containLabel: false,
      left: '16px',
      right: '2%',
      top: 48,
    };
  }

  private getDataZoomConfig(): DataZoomComponentOption[] {
    const oneMinuteInSeconds = 60;
    const tenMinutesInMilliseconds = 10 * 60 * 1000;

    return [
      {
        backgroundColor: 'transparent',
        borderColor: ColorUtilitiesService.shikensoGray40012,
        bottom: 10,
        fillerColor: ColorUtilitiesService.shikensoGray40012,
        filterMode: 'none',
        handleStyle: {
          borderColor: ColorUtilitiesService.shikensoGray400,
          color: ColorUtilitiesService.shikensoGray400,
        },
        height: 20,
        minValueSpan: this.videoTimeMode() ? oneMinuteInSeconds : tenMinutesInMilliseconds,
        moveHandleSize: 6,
        moveHandleStyle: {
          color: ColorUtilitiesService.shikensoGray400,
        },
        show: true,
        showDataShadow: false,
        showDetail: false,
        type: 'slider',
        xAxisIndex: [0],
        zoomLock: false,
      },
    ];
  }

  private getXAxisConfig(): EChartsOption['xAxis'] {
    const isVideoTimeMode = this.videoTimeMode();

    return {
      axisLabel: {
        color: ColorUtilitiesService.shikensoPurpleBase60,
        fontSize: 10,
        formatter: isVideoTimeMode
          ? (value: number): string => DurationUtilitiesService.convertSecondsToVideoTime(value, true)
          : '{HH}:{mm}',
        margin: 20,
        showMaxLabel: true,
        showMinLabel: true,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        alignWithLabel: true,
        length: 12,
        lineStyle: {
          color: ColorUtilitiesService.shikensoGray600,
        },
        show: true,
      },
      boundaryGap: false,
      max: this.maxTimestamp,
      min: this.minTimestamp,
      splitLine: {
        show: false,
      },
      splitNumber: 8,
      type: isVideoTimeMode ? 'value' : 'time',
    } as EChartsOption['xAxis'];
  }

  private getYAxisConfig(): EChartsOption['yAxis'] {
    return {
      axisLabel: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      max: 2,
      min: 0,
      nameGap: 40,
      nameLocation: 'middle',
      nameRotate: 90,
      nameTextStyle: {
        color: ColorUtilitiesService.shikensoPurpleBase,
        fontSize: 10,
        fontWeight: 'normal',
      },
      splitLine: {
        show: false,
      },
      type: 'value',
    };
  }

  private getSeriesConfig(): SeriesOption[] {
    const dotGraphData = this.processedDotGraphData();
    const isPairMode = this.pairMode();

    const dotSeriesData = this.buildSeriesData(dotGraphData, isPairMode);

    const seriesLineStyle = this.getSeriesLineStyle(isPairMode);
    const dotConfiguration = this.graphUtilitiesService.getDotConfiguration();
    const findingSpans = this.findingSpans();

    if (findingSpans) {
      const spans = sortFindingSpans(findingSpans.items);

      return [
        ...this.findingSpansChart.buildSeries(
          spans,
          spans.map((span) => [
            [span.start, 1],
            [span.end, 1],
          ]),
          this.graphUtilitiesService.getColorByStatus(findingSpans.status),
          findingSpans.legend,
          0,
        ),
        // The whole post's length under the spans, like the viewer line under the stream graph's spans.
        {
          data: [
            [this.minTimestamp, 1],
            [this.maxTimestamp, 1],
          ],
          emphasis: { disabled: true },
          id: 'findingSpanTrack',
          lineStyle: { color: ColorUtilitiesService.shikensoPinkBase40, width: 2 },
          showSymbol: false,
          silent: true,
          tooltip: { show: false },
          type: 'line',
          z: 1,
        } as LineSeriesOption,
      ];
    }

    return [
      {
        data: dotSeriesData.map((item) => ({
          ...item,
          itemStyle: {
            borderColor: ColorUtilitiesService.shikensoBaseWhite,
            borderWidth: dotConfiguration.borderWidth,
            color: this.graphUtilitiesService.getColorByStatus(item?.status ?? EAnalyticsStatus.ok),
            shadowBlur: dotConfiguration.shadowBlur,
            shadowColor: `${this.graphUtilitiesService.getColorByStatus(item?.status ?? EAnalyticsStatus.ok)}24`,
          },
        })),
        lineStyle: seriesLineStyle,
        name: 'LineData',
        showSymbol: true,
        symbol: 'circle',
        symbolSize: dotConfiguration.symbolSize,
        type: 'line',
      } as LineSeriesOption,
    ];
  }

  private buildSeriesData(data: ITimelineGraphData[], isPairMode: boolean): (TTimelineGraphSeriesItem | null)[] {
    if (!data?.length) {
      return [];
    }

    if (!isPairMode) {
      return this.buildStandardSeriesData(data);
    } else {
      return this.buildPairSeriesData(data);
    }
  }

  private buildStandardSeriesData(data: ITimelineGraphData[]): TTimelineGraphSeriesItem[] {
    const seriesData: TTimelineGraphSeriesItem[] = [];

    for (const item of data) {
      seriesData.push({ name: item.id, status: item.status, value: [item.timestamp, item.value] });
    }

    return seriesData;
  }

  private buildPairSeriesData(data: ITimelineGraphData[]): (TTimelineGraphSeriesItem | null)[] {
    const seriesData: (TTimelineGraphSeriesItem | null)[] = [];

    for (let i = 0; i < data.length; i += 2) {
      const point1 = data[i];
      const point2 = data[i + 1];

      if (!point2) {
        break;
      }

      seriesData.push({
        name: point1.id,
        status: point1.status,
        value: [point1.timestamp, point1.value],
      });
      seriesData.push({
        name: point2.id,
        status: point2.status,
        value: [point2.timestamp, point2.value],
      });

      if (i < data.length - 2) {
        seriesData.push(null);
      }
    }

    return seriesData;
  }

  private getSeriesLineStyle(isPairMode: boolean): LineSeriesOption['lineStyle'] {
    if (!isPairMode) {
      return { width: 0 };
    } else {
      return { color: ColorUtilitiesService.shikensoPinkBase, type: 'dashed', width: 2 };
    }
  }

  private calculateNextIndex(): number {
    return getNextDotIndex(this.selectedPointDataIndex, this.processedDotGraphData().length);
  }

  private panDataZoomToTimestamp(timestamp: number): void {
    if (!this.chartInstance) {
      return;
    }

    const currentOption = this.chartInstance.getOption() as EChartsOption;

    if (currentOption.dataZoom && Array.isArray(currentOption.dataZoom) && currentOption.dataZoom.length > 0) {
      const [dataZoomConfig] = currentOption.dataZoom;
      const currentStartPercent = typeof dataZoomConfig.start === 'number' ? dataZoomConfig.start : 0;
      const currentEndPercent = typeof dataZoomConfig.end === 'number' ? dataZoomConfig.end : 100;
      const currentSpanPercent = currentEndPercent - currentStartPercent;

      const totalDuration = this.maxTimestamp - this.minTimestamp;
      const currentStartTimestamp = this.minTimestamp + totalDuration * (currentStartPercent / 100);
      const currentEndTimestamp = this.minTimestamp + totalDuration * (currentEndPercent / 100);

      let newStartPercent: number | null = null;

      if (timestamp < currentStartTimestamp) {
        newStartPercent = ((timestamp - this.minTimestamp) / totalDuration) * 100 - currentSpanPercent * 0.1;
      } else if (timestamp > currentEndTimestamp) {
        newStartPercent = ((timestamp - this.minTimestamp) / totalDuration) * 100 - currentSpanPercent * 0.9;
      }

      if (newStartPercent !== null) {
        newStartPercent = Math.max(0, newStartPercent);
        let newEndPercent = newStartPercent + currentSpanPercent;

        if (newEndPercent > 100) {
          newEndPercent = 100;
          newStartPercent = Math.max(0, 100 - currentSpanPercent);
        }

        this.chartInstance.dispatchAction({
          dataZoomIndex: 0,
          end: newEndPercent,
          start: newStartPercent,
          type: 'dataZoom',
        });
      }
    }
  }

  private getTooltipFormatter(params: unknown): string {
    const text = this.videoTimeMode()
      ? this.getTooltipFormatterVideoTime(params)
      : this.getTooltipFormatterDate(params);

    return text ? this.findingSpansChart.prefixTooltip((params as { axisValue?: unknown }[])[0].axisValue, text) : text;
  }

  private getTooltipFormatterVideoTime(params: unknown): string {
    if (!Array.isArray(params) || params.length === 0) {
      return '';
    }

    const param = params[0] as { value: [number, number] };

    if (
      !param ||
      !param.value ||
      !Array.isArray(param.value) ||
      param.value.length < 2 ||
      typeof param.value[0] !== 'number' ||
      typeof param.value[1] !== 'number'
    ) {
      return '';
    }

    const [videoTime] = param.value;

    return DurationUtilitiesService.convertSecondsToVideoTime(videoTime, true);
  }

  private getTooltipFormatterDate(params: unknown): string {
    if (!Array.isArray(params) || params.length === 0) {
      return '';
    }

    const param = params[0] as { value: [number, number] };

    if (
      !param ||
      !param.value ||
      !Array.isArray(param.value) ||
      param.value.length < 2 ||
      typeof param.value[0] !== 'number' ||
      typeof param.value[1] !== 'number'
    ) {
      return '';
    }

    const [timestamp, value] = param.value;

    const timeStr: string = this.dateUtils.formatDate(new Date(timestamp), 'HH:mm');
    const valueStr: string = this.numberUtils.getNumberWithSuffix(value);

    return `${timeStr} - ${valueStr} ${this.viewersLabel}`;
  }
}
